import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

export function getPasscode() {
  const match = window?.location.search.match(/passcode=([^&]*)/);
  const passcode = match ? match[1] : window.sessionStorage.getItem('passcode');
  return passcode;
}

const uri = window.location.href;
let endpoint = 'https://sandstorm-robin-5218.twil.io/flexvideotokenizer';
if (uri?.match('-uat')) endpoint = 'https://ceil-mule-2668.twil.io/flexvideotokenizer';
if (uri?.match('-test')) endpoint = 'https://ceil-mule-2668.twil.io/flexvideotokenizer';
if (uri?.match('localhost')) endpoint = 'https://ceil-mule-2668.twil.io/flexvideotokenizer';

export async function fetchWithRetry(url: string, options: RequestInit, context: string): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
        Sentry.addBreadcrumb({
          category: 'fetchWithRetry',
          message: `Retry attempt ${attempt}/${MAX_RETRIES} for ${context}`,
          level: Sentry.Severity.Warning,
        });
      }

      const response = await fetch(url, options);

      if (response.status >= 500 && attempt < MAX_RETRIES) {
        lastError = new Error(`Server error ${response.status}: ${response.statusText}`);
        Sentry.addBreadcrumb({
          category: 'fetchWithRetry',
          message: `${context} got ${response.status}, will retry`,
          level: Sentry.Severity.Warning,
          data: { attempt, status: response.status },
        });
        continue;
      }

      return response;
    } catch (error) {
      lastError = error as Error;
      if (attempt < MAX_RETRIES) {
        Sentry.addBreadcrumb({
          category: 'fetchWithRetry',
          message: `${context} network error, will retry`,
          level: Sentry.Severity.Warning,
          data: { attempt, error: lastError.message },
        });
        continue;
      }
    }
  }

  Sentry.captureException(lastError, {
    tags: { context, endpoint: url },
    extra: { maxRetries: MAX_RETRIES, finalError: lastError?.message },
  });
  throw lastError;
}

export function fetchToken(
  name: string,
  room: string,
  passcode: string,
  create_room = true,
  create_conversation = process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true'
) {
  const body = {
    user_identity: name,
    room_name: room,
    verify_passcode: true,
    passcode,
    create_room,
    create_conversation,
  };

  return fetchWithRetry(
    endpoint,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    'fetchToken'
  );
}

export async function verifyPasscode(passcode: string): Promise<{ isValid: boolean; error?: string }> {
  try {
    const res = await fetchToken('temp-name', 'temp-room', passcode, false, false);
    const jsonResponse = await res.json();

    if (res.status === 401) {
      return { isValid: false, error: jsonResponse.error?.message || 'Unauthorized' };
    }

    if (res.ok && jsonResponse.token) {
      return { isValid: true };
    }

    Sentry.captureMessage('verifyPasscode: unexpected response', {
      level: 'error' as Sentry.Severity,
      extra: { status: res.status, body: jsonResponse },
    });
    return { isValid: false, error: jsonResponse.error?.message || `Unexpected response (${res.status})` };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: 'verifyPasscode' },
    });
    return { isValid: false, error: 'Network error. Please check your connection and try again.' };
  }
}

export function getErrorMessage(message: string) {
  switch (message) {
    case 'passcode incorrect':
      return 'Passcode is incorrect';
    case 'passcode expired':
      return 'Passcode has expired';
    default:
      return message;
  }
}

export default function usePasscodeAuth() {
  const history = useHistory();

  const [user, setUser] = useState<{ displayName: undefined; photoURL: undefined; passcode: string } | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const getToken = useCallback(
    async (name: string, room: string) => {
      try {
        const res = await fetchToken(name, room, user!.passcode);
        if (res.ok) {
          return res.json();
        }
        const json = await res.json();
        const errorMessage = getErrorMessage(json.error?.message || res.statusText);
        throw new Error(errorMessage);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { context: 'getToken' },
          extra: { name, room },
        });
        throw error;
      }
    },
    [user]
  );

  const updateRecordingRules = useCallback(
    async (room_sid, rules) => {
      const res = await fetchWithRetry(
        endpoint,
        {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ room_sid, rules, passcode: user?.passcode }),
          method: 'POST',
        },
        'updateRecordingRules(passcode-auth)'
      );
      const jsonResponse = await res.json();

      if (!res.ok) {
        const error = new Error(jsonResponse.error?.message || 'There was an error updating recording rules');
        error.code = jsonResponse.error?.code;
        return Promise.reject(error);
      }

      return jsonResponse;
    },
    [user]
  );

  useEffect(() => {
    const passcode = getPasscode();

    if (passcode) {
      window.sessionStorage.setItem('searchStr', window.location.search?.slice(1).trim());
      verifyPasscode(passcode)
        .then(verification => {
          if (verification.isValid) {
            setUser({ passcode } as any);
            window.sessionStorage.setItem('passcode', passcode);
            // Drop the query string (it holds the passcode) but stay on the current route. This must be the
            // router-relative path: window.location.pathname includes the /video-website basename, which sent the
            // app to an unknown route and remounted the whole app via the catch-all <Redirect>.
            history.replace(history.location.pathname);
          } else {
            window.sessionStorage.removeItem('searchStr');
          }
        })
        .catch(error => {
          Sentry.captureException(error, { tags: { context: 'usePasscodeAuth.init' } });
        })
        .finally(() => setIsAuthReady(true));
    } else {
      setIsAuthReady(true);
    }
  }, [history]);

  const signIn = useCallback((passcode: string) => {
    return verifyPasscode(passcode).then(verification => {
      if (verification.isValid) {
        setUser({ passcode } as any);
        window.sessionStorage.setItem('passcode', passcode);
      } else {
        throw new Error(getErrorMessage(verification.error || 'Authentication failed'));
      }
    });
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    window.sessionStorage.removeItem('passcode');
    window.sessionStorage.removeItem('searchStr');
    return Promise.resolve();
  }, []);

  return { user, isAuthReady, getToken, signIn, signOut, updateRecordingRules };
}
